import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'

export type Language = 'en' | 'ar'
export type Direction = 'ltr' | 'rtl'
export type DateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD'
export type TimeFormat = '12h' | '24h'
export type Currency = 'USD' | 'EUR' | 'SAR' | 'AED'

export interface SettingsState {
  // Localization
  language: Language
  direction: Direction
  
  // Formatting preferences
  dateFormat: DateFormat
  timeFormat: TimeFormat
  currency: Currency
  timezone: string
  
  // Display preferences
  compactMode: boolean
  showTooltips: boolean
  animationsEnabled: boolean
  
  // Data preferences
  pageSize: number
  autoRefresh: boolean
  autoRefreshInterval: number // in seconds
  
  // Actions
  setLanguage: (language: Language) => void
  setDirection: (direction: Direction) => void
  setDateFormat: (format: DateFormat) => void
  setTimeFormat: (format: TimeFormat) => void
  setCurrency: (currency: Currency) => void
  setTimezone: (timezone: string) => void
  setCompactMode: (compact: boolean) => void
  setShowTooltips: (show: boolean) => void
  setAnimationsEnabled: (enabled: boolean) => void
  setPageSize: (size: number) => void
  setAutoRefresh: (enabled: boolean) => void
  setAutoRefreshInterval: (interval: number) => void
  resetToDefaults: () => void
}

const defaultSettings = {
  language: 'en' as Language,
  direction: 'ltr' as Direction,
  dateFormat: 'MM/DD/YYYY' as DateFormat,
  timeFormat: '12h' as TimeFormat,
  currency: 'USD' as Currency,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  compactMode: false,
  showTooltips: true,
  animationsEnabled: true,
  pageSize: 10,
  autoRefresh: false,
  autoRefreshInterval: 30,
}

/**
 * Settings store for user preferences and localization
 * Persists user preferences across sessions
 */
export const useSettingsStore = create<SettingsState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state from defaults
        ...defaultSettings,

        // Language and direction actions
        setLanguage: (language: Language) => {
          // Auto-set direction based on language
          const direction: Direction = language === 'ar' ? 'rtl' : 'ltr'
          
          set(
            { language, direction },
            false,
            'settings/setLanguage'
          )
          
          // Update document direction
          document.documentElement.dir = direction
          
          // Update document language
          document.documentElement.lang = language
        },

        setDirection: (direction: Direction) => {
          set({ direction }, false, 'settings/setDirection')
          document.documentElement.dir = direction
        },

        // Format preference actions
        setDateFormat: (dateFormat: DateFormat) => {
          set({ dateFormat }, false, 'settings/setDateFormat')
        },

        setTimeFormat: (timeFormat: TimeFormat) => {
          set({ timeFormat }, false, 'settings/setTimeFormat')
        },

        setCurrency: (currency: Currency) => {
          set({ currency }, false, 'settings/setCurrency')
        },

        setTimezone: (timezone: string) => {
          set({ timezone }, false, 'settings/setTimezone')
        },

        // Display preference actions
        setCompactMode: (compactMode: boolean) => {
          set({ compactMode }, false, 'settings/setCompactMode')
        },

        setShowTooltips: (showTooltips: boolean) => {
          set({ showTooltips }, false, 'settings/setShowTooltips')
        },

        setAnimationsEnabled: (animationsEnabled: boolean) => {
          set({ animationsEnabled }, false, 'settings/setAnimationsEnabled')
        },

        // Data preference actions
        setPageSize: (pageSize: number) => {
          set({ pageSize }, false, 'settings/setPageSize')
        },

        setAutoRefresh: (autoRefresh: boolean) => {
          set({ autoRefresh }, false, 'settings/setAutoRefresh')
        },

        setAutoRefreshInterval: (autoRefreshInterval: number) => {
          set({ autoRefreshInterval }, false, 'settings/setAutoRefreshInterval')
        },

        // Reset to defaults
        resetToDefaults: () => {
          set(defaultSettings, false, 'settings/resetToDefaults')
          document.documentElement.dir = defaultSettings.direction
          document.documentElement.lang = defaultSettings.language
        },
      }),
      {
        name: 'settings-storage',
        storage: {
          getItem: (name: string) => {
            const value = localStorage.getItem(name)
            return value ? JSON.parse(value) : null
          },
          setItem: (name: string, value: any) => {
            localStorage.setItem(name, JSON.stringify(value))
          },
          removeItem: (name: string) => {
            localStorage.removeItem(name)
          },
        },
      }
    ),
    {
      name: 'settings-store',
    }
  )
)
